/* eslint-disable import-extensions/file-extension-required */
import type { DataProxy } from 'apollo-cache';
import set from 'lodash/set';

import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { getLogger } from '@confluence/logger';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper.ts';
import {
	type CommentType as CommentsType,
	type ActiveCommentsQueryType,
	type ActiveCommentsQueryVariables,
	type ActiveCommentsPanelNode,
	type ActiveCommentsPanelReply,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { CommentType } from '@confluence/comments-util/entry-points/analytics.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes';

import { DEFAULT_LIMIT } from './index.ts';

import type { CreateGeneralReplyMutation as CreateGeneralReplyMutationData } from './__types__/CreateGeneralReplyMutation.ts';
import type { CreateGeneralCommentMutation as CreateGeneralCommentMutationData } from './__types__/CreateGeneralCommentMutation.ts';
import type { DeleteGeneralCommentMutation as DeleteGeneralCommentMutationData } from './__types__/DeleteGeneralCommentMutation.ts';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from './CommentsSectionQuery.graphql.ts';
import type { CommentsSectionQuery as CommentsSectionQueryType } from './__types__/CommentsSectionQuery.ts';
import type { CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType } from './__types__/CommentsSectionQueryWithoutReactionsQuery.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

const logger = getLogger('page-comments-queries');

export type UpdateApolloCacheParentCallbackDataType = {
	data?: CreateGeneralCommentMutationData | DeleteGeneralCommentMutationData | boolean | null;
};

export const updateApolloCacheParentCallback =
	(
		actionType: 'delete' | 'create',
		queryVariables: ActiveCommentsQueryVariables,
		isReactionsEnabled?: boolean,
		commentId?: string,
	) =>
	(_cache: DataProxy, result: UpdateApolloCacheParentCallbackDataType) => {
		// If the mutation fails, don't do anything
		if (!result || !result.data) {
			return;
		}

		const { pageId, type } = queryVariables;

		try {
			const singleType = (type && type.length >= 1 ? type[0] : undefined) as CommentsType;
			const consistentQueryVariables = buildConsistentActiveCommentsQueryVariables({
				pageId,
				type: singleType,
			});

			// Update the comments panel data
			const commentsPanelCacheResponse = readQueryInterop<ActiveCommentsQueryType>({
				query: ActiveCommentsQuery,
				variables: consistentQueryVariables,
			}) as ApolloInteropMutableHelper<ActiveCommentsQueryType>;

			if (commentsPanelCacheResponse) {
				const panelNodes: ApolloInteropMutableHelper<ActiveCommentsPanelNode[]> =
					commentsPanelCacheResponse?.comments?.nodes || [];

				if (actionType === 'delete') {
					// Remove the returned commentId from the cache and reset it
					const idxToRemove = panelNodes.findIndex((comment) => comment?.id === commentId);

					if (idxToRemove !== -1) {
						panelNodes.splice(idxToRemove, 1);
					}
				} else {
					const mutationResult = {
						...(result.data as CreateGeneralCommentMutationData).createFooterComment,
						replies: [],
					} as ApolloInteropMutableHelper<ActiveCommentsPanelNode>;

					// Add the new reply entry to the cache and reset it
					panelNodes.push(mutationResult);
				}

				writeQueryInterop({
					query: ActiveCommentsQuery,
					variables: consistentQueryVariables,
					data: commentsPanelCacheResponse,
				});
			}
		} catch (err) {
			logger.error`An Error occurred when updating comments panel cache for ${actionType} - ${err}`;
		}
		try {
			const commentsSectionQuery = isReactionsEnabled
				? CommentsSectionQuery
				: CommentsSectionWithoutReactionsQuery;

			// Update the comments section data
			const commentsSectionCacheResponse = readQueryInterop<
				CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType
			>({
				query: commentsSectionQuery,
				variables: {
					contentId: pageId,
					offset: '',
					first: DEFAULT_LIMIT,
				},
			});

			if (commentsSectionCacheResponse) {
				// grab the parent comment for the comments panel
				const commentSectionCacheDataClone = { ...commentsSectionCacheResponse };
				const commentEdges = [...(commentSectionCacheDataClone?.comments?.edges || [])];

				if (actionType === 'delete') {
					// Find the last index of the top level comment to delete
					const indexToDelete = commentEdges?.findIndex((edge) => edge?.node?.id === commentId);

					if (indexToDelete !== -1) {
						commentEdges.splice(indexToDelete, 1);
					}
				} else {
					const mutationResult = {
						...(result.data as CreateGeneralCommentMutationData).createFooterComment,
					};

					// As far as I can tell, this is always called w/o reactions enabled
					// This maps the mutation result we get to the cache object that's expected by the comments section query
					const cacheCommentObject = {
						id: mutationResult.id,
						ancestors: [],
						author: {
							...mutationResult.author,
							type: 'known',
						},
						body: mutationResult.body,
						contentStatus: mutationResult.contentStatus,
						links: {
							webui: mutationResult.links.webui,
							// @ts-ignore
							__typename: mutationResult.links.__typename,
						},
						location: mutationResult.location,
						parentId: mutationResult.parentId,
						permissions: mutationResult.permissions,
						version: {
							...mutationResult.version,
							friendlyWhen: 'Just now', // TODO: Maybe replace this with the actual date
							by: {
								type: 'user',
								displayName: '',
								accountId: '',
								__typename: 'User',
							},
							__typename: 'Version',
						},
						__typename: 'Comment',
					};

					// The cache will throw an error if we don't do this ¯\_(ツ)_/¯
					const cacheContentDeepCopy = JSON.parse(JSON.stringify(cacheCommentObject));

					const newEdge = {
						cursor: (commentEdges.length - 1).toString(),
						node: cacheContentDeepCopy as any,
						__typename: 'CommentEdge',
					} as any;

					const insertAfterIndex = commentEdges.length - 1;
					// Put the new reply in the array
					commentEdges.splice(insertAfterIndex + 1, 0, newEdge as any);
				}

				writeQueryInterop({
					query: commentsSectionQuery,
					variables: {
						contentId: pageId,
						offset: '',
						first: DEFAULT_LIMIT,
					},
					data: {
						...commentSectionCacheDataClone,
						comments: {
							...commentSectionCacheDataClone.comments,
							totalCount: commentEdges.length,
							pageInfo: {
								...commentSectionCacheDataClone.comments?.pageInfo,
								endCursor: actionType === 'delete' ? '' : (commentEdges.length - 1).toString(),
								hasNextPage: false,
							},
							edges: commentEdges,
						},
					},
				});
			}
		} catch (err) {
			logger.error`An Error occurred when updating comments section cache for ${actionType} - ${err}`;
		}
	};

export type UpdateApolloCacheReplyCallbackDataType = {
	data?: CreateGeneralReplyMutationData | DeleteGeneralCommentMutationData | boolean | null;
};

export const updateApolloCacheReplyCallback =
	(
		actionType: 'delete' | 'create',
		queryVariables: ActiveCommentsQueryVariables,
		parentCommentId: string,
		isReactionsEnabled?: boolean,
		commentId?: string,
	) =>
	(_cache: DataProxy, result: UpdateApolloCacheReplyCallbackDataType) => {
		// If the mutation fails, don't do anything
		if (!result || !result.data) {
			return;
		}

		const { pageId, type } = queryVariables;

		try {
			const singleType = (type && type.length >= 1 ? type[0] : undefined) as CommentsType;
			const consistentQueryVariables = buildConsistentActiveCommentsQueryVariables({
				pageId,
				type: singleType,
			});

			// Update the comments panel data
			const commentsPanelCacheResponse = readQueryInterop<ActiveCommentsQueryType>({
				query: ActiveCommentsQuery,
				variables: consistentQueryVariables,
			}) as ApolloInteropMutableHelper<ActiveCommentsQueryType>;

			if (commentsPanelCacheResponse) {
				// grab the parent comment for the comments panel
				const commentsPanelParentComment = commentsPanelCacheResponse?.comments?.nodes?.find(
					(comment) => comment?.id === parentCommentId,
				);

				if (commentsPanelParentComment) {
					if (actionType === 'delete') {
						// Remove the returned commentId from the cache and reset it
						const idxToRemove = commentsPanelParentComment.replies.findIndex(
							(reply) => reply?.id === commentId,
						);

						if (idxToRemove !== -1) {
							commentsPanelParentComment.replies.splice(idxToRemove, 1);
						}
					} else {
						const mutationResult = (result.data as CreateGeneralReplyMutationData)
							.createFooterComment as ApolloInteropMutableHelper<ActiveCommentsPanelReply>;
						// Add the new reply entry to the cache and reset it
						commentsPanelParentComment.replies.push(mutationResult);
					}

					writeQueryInterop({
						query: ActiveCommentsQuery,
						variables: consistentQueryVariables,
						data: commentsPanelCacheResponse,
					});
				}
			}
		} catch (err) {
			logger.error`An Error occurred when updating comments panel cache for ${actionType} reply - ${err}`;
		}

		try {
			const commentsSectionQuery = isReactionsEnabled
				? CommentsSectionQuery
				: CommentsSectionWithoutReactionsQuery;

			// Update the comments section data
			const commentsSectionCacheResponse = readQueryInterop<
				CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType
			>({
				query: commentsSectionQuery,
				variables: {
					contentId: pageId,
					offset: '',
					first: DEFAULT_LIMIT,
				},
			});

			if (commentsSectionCacheResponse) {
				// grab the parent comment for the comments panel
				const commentsSectionCacheData = { ...commentsSectionCacheResponse };
				const commentEdges = [...(commentsSectionCacheData?.comments?.edges || [])];
				const parentCommentIdx = commentEdges?.findIndex(
					(commentEdge) => commentEdge?.node?.id === parentCommentId,
				);

				if (parentCommentIdx !== -1) {
					if (actionType === 'delete') {
						// Find the last index of the comment to delete
						const indexToDelete = commentEdges?.findIndex((edge) => edge?.node?.id === commentId);

						if (indexToDelete !== -1) {
							commentEdges.splice(indexToDelete, 1);
						}
					} else {
						const mutationResult = {
							...(result.data as CreateGeneralReplyMutationData).createFooterComment,
						};

						// As far as I can tell, this is always called w/o reactions enabled
						// This maps the mutation result we get to the cache object that's expected by the comments section query
						const cacheCommentObject = {
							id: mutationResult.id,
							ancestors: [
								{
									id: mutationResult.parentId,
									__typename: 'Comment',
								},
							],
							author: {
								...mutationResult.author,
								type: 'known',
							},
							body: mutationResult.body,
							contentStatus: mutationResult.contentStatus,
							links: {
								webui: mutationResult.links.webui,
								// @ts-ignore
								__typename: mutationResult.links.__typename,
							},
							location: mutationResult.location,
							parentId: mutationResult.parentId,
							permissions: mutationResult.permissions,
							version: {
								...mutationResult.version,
								friendlyWhen: 'Just now', // TODO: Maybe replace this with the actual date
								by: {
									type: 'user',
									displayName: '',
									accountId: '',
									__typename: 'User',
								},
								__typename: 'Version',
							},
							__typename: 'Comment',
						};

						// The cache will throw an error if we don't do this ¯\_(ツ)_/¯
						const cacheContentDeepCopy = JSON.parse(JSON.stringify(cacheCommentObject));

						const newEdge = {
							cursor: (commentEdges.length - 1).toString(),
							node: cacheContentDeepCopy as any,
							__typename: 'CommentEdge',
						} as any;

						// Find the last index of the thread to insert the reply
						const threadLastCommentIdx = commentEdges?.findLastIndex(
							(edge) => edge?.node?.parentId === parentCommentId,
						);

						// If we can't find it, the parent has no replies
						const insertAfterIndex =
							threadLastCommentIdx !== -1 ? threadLastCommentIdx : parentCommentIdx;

						// Put the new reply in the array
						commentEdges.splice(insertAfterIndex + 1, 0, newEdge as any);
					}

					writeQueryInterop({
						query: commentsSectionQuery,
						variables: {
							contentId: pageId,
							offset: '',
							first: DEFAULT_LIMIT,
						},
						data: {
							...commentsSectionCacheData,
							comments: {
								...commentsSectionCacheData.comments,
								totalCount: commentEdges.length,
								pageInfo: {
									...commentsSectionCacheData.comments?.pageInfo,
									endCursor: actionType === 'delete' ? '' : (commentEdges.length - 1).toString(),
									hasNextPage: false,
								},
								edges: commentEdges,
							},
						},
					});
				}
			}
		} catch (err) {
			logger.error`An Error occurred when updating general comments cache for ${actionType} reply - ${err}`;
		}
	};

export const updateFooterApolloCacheOnEdit = (
	commentId: string,
	newBody: string,
	pageId: string,
	isReactionsEnabled: boolean,
	_cache: DataProxy,
	sessionDisplayName: string | null,
) => {
	const commentsSectionQuery = isReactionsEnabled
		? CommentsSectionQuery
		: CommentsSectionWithoutReactionsQuery;

	try {
		// Update the comments section data
		const commentsSectionCacheData = readQueryInterop<
			CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType
		>({
			query: commentsSectionQuery,
			variables: {
				contentId: pageId,
				offset: '',
				first: DEFAULT_LIMIT,
			},
		});

		if (commentsSectionCacheData) {
			// grab the parent comment for the comments panel
			const commentEdges = [...(commentsSectionCacheData?.comments?.edges || [])];
			const commentIdx = commentEdges?.findIndex(
				(commentEdge) => commentEdge?.node?.id === commentId,
			);

			if (
				commentIdx !== undefined &&
				commentIdx !== null &&
				commentIdx !== -1 &&
				commentEdges[commentIdx]?.node?.body &&
				commentEdges[commentIdx]?.node?.version?.number
			) {
				// Find the index to update. TS will throw an error even though we've already checked the index is valid
				// @ts-ignore
				commentEdges[commentIdx].node.body.value = newBody; // Update the body we keep in the footer cache
				// @ts-ignore
				commentEdges[commentIdx].node.version.number += 1; // This is required for the propagated footer version to also be editable
				// @ts-ignore
				set(commentEdges[commentIdx].node, 'version.by.displayName', sessionDisplayName); // Update the display name to the user who edited the comment
				// @ts-ignore
				set(commentEdges[commentIdx].node, 'version.when', new Date().toISOString()); // Update the version date to the date of the edit
			}

			writeQueryInterop({
				query: commentsSectionQuery,
				variables: {
					contentId: pageId,
					offset: '',
					first: DEFAULT_LIMIT,
				},
				data: {
					...commentsSectionCacheData,
					comments: {
						...commentsSectionCacheData.comments,
						totalCount: commentEdges.length,
						pageInfo: {
							...commentsSectionCacheData.comments?.pageInfo,
							endCursor: Math.random().toString(), // The cache won't update without this. Should be harmless, given hasNextPage is false, so we won't be loading more
							hasNextPage: false,
						},
						edges: commentEdges,
					},
				},
			});
		}
	} catch (err) {
		logger.error`An Error occurred when updating cache for edit footer reply - ${err}`;
	}
};

interface HandleCreateGeneralReplySuccessProps {
	onSuccess: any;
	editCommentQueryId?: string;
	setCommentForEdit?: any;
	data: any;
	createAnalyticsEvent: any;
	pageId: string | undefined;
	pageType: string;
	analyticsSource: string;
	commentId: string | undefined;
	pageMode: PageMode;
	experienceTracker: ExperienceTrackerAPI;
	searchSessionId?: string | null;
	isFooterComment?: boolean;
}

export const handleCreateReplySuccess = ({
	onSuccess,
	editCommentQueryId,
	setCommentForEdit,
	data,
	createAnalyticsEvent,
	pageId,
	pageType,
	analyticsSource,
	commentId,
	pageMode,
	experienceTracker,
	searchSessionId,
	isFooterComment,
}: HandleCreateGeneralReplySuccessProps) => {
	onSuccess();
	if (editCommentQueryId) {
		setCommentForEdit && setCommentForEdit('');
	}

	const replyCommentInfo = data?.createFooterComment;

	// Find the reply button container and scroll to it if needed
	if (replyCommentInfo?.id && commentId && isFooterComment) {
		// First find the container
		const container = document.getElementById(`footer-comment-thread-${commentId}-container`);

		if (container) {
			// Set up observer to watch for the reply button in the container
			const observer = new MutationObserver((_mutations, obs) => {
				const replyButtonContainer = container.querySelector(
					'[data-testid="comments-panel-reply-comment-action"]',
				);

				if (replyButtonContainer) {
					obs.disconnect();

					// Check if the element is not in viewport, accounting for 75px add comment prompt at bottom
					const rect = replyButtonContainer.getBoundingClientRect();
					const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
					const isInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= viewportHeight - 75 && // Account for add comment prompt
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);

					if (!isInViewport) {
						replyButtonContainer.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
					}
				}
			});

			observer.observe(container, {
				childList: true,
				subtree: true,
			});

			// Cleanup observer after 2 seconds to prevent memory leaks
			setTimeout(() => {
				observer.disconnect();
			}, 2000);
		}
	}

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'created',
			actionSubject: 'comment',
			actionSubjectId: replyCommentInfo?.id, // The newly created comment ID
			objectType: pageType,
			objectId: pageId,
			source: analyticsSource,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				commentType: CommentType.GENERAL,
				source: analyticsSource,
				pageType,
				isReply: true,
				pageMode,
				isLivePage: pageMode === PageMode.LIVE,
				editor: 'v2',
				searchSessionId,
				navdexPointType: END,
				parentCommentId: commentId ?? null, // analytics event schema type expects string or null
			},
		},
	}).fire();

	experienceTracker.succeed({
		name: REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	});
};
